import type { SessionState } from './session-state'

/**
 * Lulu 工作台与后端之间的合同边界。
 *
 * # 为什么这个文件存在
 *
 * 设计交接包 `SPEC-routes-api.md` 列了 14 个端点。2026-08-25 对着 `origin/main`
 * 与编排内核候选 `2d2ad2aa` 逐条核过：能用的只有 1 个，另有 1 个存在但请求体
 * 对不上，其余 12 个不存在。
 *
 * 在这种情况下，界面有两条路可走：拿示例数据把屏填满，或者如实说没接上。
 *
 * **第一条路更危险，而且危险的方式很隐蔽**：一个填满了数据的工作台看起来是
 * 好的，于是没有人会去数还差几个接口，验收会对着假数据一条条打勾，
 * 而「这里其实没有后端」这件事只有写它的人知道。这个产品整套设计就是围绕
 * 「让人分不清哪些是真发生的」这个失败模式做的——在自己的接线上重演它，
 * 比任何一个界面 bug 都贵。
 *
 * 所以缺口在这里是一等公民：一张会被用例核对的表。
 *
 * # 为什么表里没有一句中文
 *
 * 「这一条为什么缺、后端那边长什么样、该怎么办」全部写在同目录的
 * `BACKEND-GAPS.md` 里，代码里只留机器用得上的事实。
 *
 * 两个原因，缺一不可：
 *
 *   一是那些话**本来就是给后端 owner 的交付物**，不是给渲染层的数据。
 *     把一份交接文档拆成几十个字符串常量塞进屏目录，读的人得先把它拼回去。
 *   二是 `make check-hardcoded-copy` 会把屏文件里的中文字面量逐条判红，
 *     而它判得对：一个屏文件里的中文，从外面看不出是给用户的还是给同事的。
 *
 * 文档与这张表由 `backend-contract.test.ts` 交叉核对，
 * 所以那份文档不会悄悄过期——这正是「一条没人守的断言迟早被删掉」的解法。
 */

/** 一个端点此刻的真实状态。 */
export type EndpointStatus =
  /** 后端有，形状也对得上，可以直接用。 */
  | 'wired'
  /** 后端有这条路由，但请求体或语义与设计稿要的不是一回事。 */
  | 'shape_mismatch'
  /** 后端没有。界面必须如实说没接上。 */
  | 'missing'

export interface EndpointGap {
  /** 设计稿 SPEC-routes-api.md 里写的那个端点，逐字。 */
  readonly contract: string
  readonly status: EndpointStatus
}

/**
 * 14 条端点逐条核对的结果。每一条的来龙去脉见 `BACKEND-GAPS.md` 的同名行。
 */
export const ENDPOINT_GAPS: readonly EndpointGap[] = [
  // 2026-08-26：后端合同 09e4b825 落地，**14 条全部到位**。
  //
  // 这张表刻意留着而不是删掉：它是「现在都通了」这句话唯一有人守着的
  // 落点。删了之后，下一次谁改坏一条，没有任何东西会红——
  // 而一张全绿的表比没有表更有价值，正因为它会在不全绿的那天说话。
  { contract: 'POST /conversations', status: 'wired' },
  { contract: 'POST /conversations/:id/bind', status: 'wired' },
  { contract: 'GET /conversations/:id/stream', status: 'wired' },
  { contract: 'POST /conversations/:id/model', status: 'wired' },
  { contract: 'GET /agents?scope=account|project', status: 'wired' },
  { contract: 'POST /agents', status: 'wired' },
  { contract: 'POST /conversations/:id/team', status: 'wired' },
  { contract: 'POST /workitems/:id/subruns', status: 'wired' },
  { contract: 'DELETE /subruns/:id', status: 'wired' },
  { contract: 'POST /decisions/:id/answer', status: 'wired' },
  { contract: 'GET /projects/recent?limit=6', status: 'wired' },
  { contract: 'POST /projects', status: 'wired' },
  { contract: 'GET /projects/:id/init-status', status: 'wired' },
  { contract: 'POST /environments/checkout', status: 'wired' },
] as const

/** 这一条接上了没有。接上了才允许去调它。 */
export function endpointStatus(contract: string): EndpointStatus | null {
  return ENDPOINT_GAPS.find((g) => g.contract === contract)?.status ?? null
}

/**
 * 会话形态：设计稿说 `solo`，数据库说 `single`。
 *
 * 这不是措辞差异，是**一个会让写入被拒的 CHECK 约束**：
 * `agent_conversations_collaboration_mode CHECK (collaboration_mode IN ('single','team'))`。
 * 照设计稿的词直接发过去，那一行会被数据库拒掉。
 *
 * 两边各留各的词、在这一层显式换：界面上的词跟设计稿走（产品合同），
 * 线上的词跟数据库走（写得进去）。**刻意不在界面里改用 `single`**——
 * 那等于让数据库的实现细节渗进产品语言，而这个产品的语言是它的核心差异化。
 */
export type ConversationMode = 'solo' | 'team'

// 2026-08-26 收口时删掉了 `toWireMode` / `fromWireMode` 与
// `WireCollaborationMode`。
//
// **它们在的时候，同一件事有两份词表**：界面说 solo、线上发 single，
// 而翻译写在前端。现在后端把 wire 定成 `solo|team`（openapi
// Conversation.collaboration_mode 的注释逐字：「wire 说前台语义
// 'solo'|'team'；库里的 'single' 在出口翻译」），翻译回到了它该在的
// 那一侧——**离库最近的那一侧**。
//
// 留着那两个函数的害处不是多几行：一个前端的翻译层会在后端改词表的
// 那天悄悄失配，而失配的表现是「团队会话被当成单 Agent」——
// 右上角那个团队入口凭空消失，没有任何东西会红。

/**
 * 内核 `lulu_runtime_sessions.state` 的 14 档。
 *
 * 从迁移 000105 的 CHECK 约束逐字抄来。它与设计稿那 12 档**不是同一套**：
 * 内核这一套描述的是「一个 Agent 的运行时会话此刻在哪一步」，
 * 设计稿那一套描述的是「用户在界面上该看到哪句话」。
 */
export const KERNEL_SESSION_STATES = [
  'provisioning',
  'ready',
  'running',
  'waiting_agent',
  'waiting_user',
  'blocked',
  'disconnected',
  'recovering',
  'platform_held',
  'reassigned',
  'fenced',
  'completed',
  'failed',
  'cancelled',
] as const

export type KernelSessionState = (typeof KERNEL_SESSION_STATES)[number]

/**
 * 内核 14 档 → 设计稿 12 档。映射不到就是 `null`。
 *
 * 为什么不兜底、以及那 5 档各自的理由，见 `BACKEND-GAPS.md`。
 * 一句话版本：往上凑会说一件没发生的事，往下兜成 `unknown` 会把唯一一个
 * 诚实的档位变成垃圾桶。
 */
const KERNEL_TO_DESIGN: Partial<Record<KernelSessionState, SessionState>> = {
  running: 'running',
  waiting_agent: 'waiting_agent',
  waiting_user: 'waiting_user',
  blocked: 'blocked',
  disconnected: 'disconnected',
  recovering: 'recovering',
  platform_held: 'platform_held',
  completed: 'completed',
  failed: 'failed',
}

/**
 * 设计稿那 12 档里没有对应说法的内核状态。
 *
 * 这是一份**要拿去问设计和后端的清单**，不是一个可以一直挂着的默认行为。
 * 每一档为什么对不上，见 `BACKEND-GAPS.md` 的同名表。
 */
export const UNMAPPED_KERNEL_STATES: readonly KernelSessionState[] = [
  'provisioning',
  'ready',
  'reassigned',
  'fenced',
  'cancelled',
] as const

/** 映射不过去就返回 null。**不要在这里兜底**，理由见上面那段。 */
export function mapKernelSessionState(kernel: string): SessionState | null {
  return KERNEL_TO_DESIGN[kernel as KernelSessionState] ?? null
}

/**
 * **字段级的缺口，与端点级分开列。**
 *
 * 分开是因为两者的修法与影响完全不同：一条端点不存在，整条路都不通；
 * 一个字段缺了，路是通的、只是少带一样东西。把后者混进端点表，
 * 读的人会以为「建会话」这件事根本做不了，然后去重建一个已经有的东西——
 * 而这正是 2026-08-25 那次判重之后真实发生的风险。
 *
 * 也不能反过来把它省掉：mode 缺了是**用户看得见的**（刷新之后
 * 单 Agent / 团队的选择回不来），界面必须如实说出来。
 */
export interface FieldGap {
  /** 哪条端点上的哪个字段。 */
  readonly on: string
  readonly field: string
  /** 界面为此要说的那句话。**必须有**——缺了字段而界面不说，就是骗人。 */
  readonly copyId: 'lulu_workbench.launch.mode_not_persisted'
}

export const FIELD_GAPS: readonly FieldGap[] = [
  // 空了。`mode` 那条在 09e4b825 落地时关掉——
  // `CreateConversationRequest.collaboration_mode` 是真字段了。
  //
  // 这个空数组不是可以删的：它和上面那张全绿的表是同一个道理，
  // 而且它比表更容易被误删——一个空列表看起来像「没用上的代码」。
]
